/**
 * useAuth() — the canonical React hook for auth.
 *
 * Exposes the current `AuthSnapshot` (subscribed via `useSyncExternalStore`
 * so concurrent-mode-safe) PLUS a set of pre-bound action creators that:
 *   - call the Plan 04 pure action (login / completeOtp / platformLogin / logout)
 *   - on success, commit the resulting snapshot back into the store
 *
 * Callers hit `const auth = useAuth(); auth.login({ ... })` without thinking
 * about the store. `requestReset` + `completeReset` are re-exported as-is
 * because they don't mutate the snapshot (AUTH-09 enumeration defense — the
 * UI transitions via its own local state).
 *
 * `logout` never commits a snapshot locally because Plan 04's `performLogout`
 * ends with `window.location.assign(loginPath)` — a hard nav resets all React
 * state, so the next paint is a fresh app in UNAUTH state.
 */
import { useSyncExternalStore, useCallback } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useAuthStore, useAuthAudience } from "./AuthProvider.js";
import type { AuthSnapshot, Audience } from "../types.js";
import { UNAUTH_SNAPSHOT } from "../types.js";
import {
  performLogin,
  performGoogleLogin,
  performMicrosoftLogin,
  completeOtp as completeOtpAction,
  verifyTotp as verifyTotpAction,
  performLogout as performLogoutAction,
  requestReset as requestResetAction,
  completeReset as completeResetAction,
  performPlatformLogin,
  performInviteAccept as performInviteAcceptAction,
  performWorkspaceSelect,
  type LoginResult,
  type LoginArgs,
  type GoogleLoginArgs,
  type MicrosoftLoginArgs,
  type CompleteOtpArgs,
  type VerifyTotpArgs,
  type PerformLogoutArgs,
  type RequestResetArgs,
  type CompleteResetArgs,
  type PlatformLoginArgs,
  type InviteAcceptArgs,
  type WorkspaceSelectArgs,
} from "../actions/index.js";

// Callers don't pass `audience` — useAuth injects it from context so
// per-app main.tsx remains the single source of truth (createAppRoot({ aud })).
//
// Except one: the global login at `app.[staging.]hr.linkco.jo` has no audience
// of its own. It cannot know whether the person typing is an HR admin or an
// employee until the backend says so, so it tries `admin` and, on a refusal,
// the same credentials as `mobile`. That is a property of the ATTEMPT, not of
// the bundle, so these args accept an explicit override. Omit it — as the
// three per-audience apps do — and the context audience still wins.
export type UseAuthLoginArgs = Omit<LoginArgs, "audience"> & { audience?: Audience };
export type UseAuthGoogleLoginArgs = Omit<GoogleLoginArgs, "audience"> & { audience?: Audience };
export type UseAuthMicrosoftLoginArgs = Omit<MicrosoftLoginArgs, "audience"> & {
  audience?: Audience;
};
export type UseAuthInviteAcceptArgs = Omit<InviteAcceptArgs, "audience">;
export type UseAuthCompleteOtpArgs = Omit<CompleteOtpArgs, "audience"> & { audience?: Audience };
export type UseAuthCompleteTotpArgs = Omit<VerifyTotpArgs, "audience"> & { audience?: Audience };
export type UseAuthSelectWorkspaceArgs = Omit<WorkspaceSelectArgs, "audience">;

export interface UseAuthApi extends AuthSnapshot {
  /**
   * Tenant-admin / employee login (CC-03 discriminated union).
   * Returns `{kind: "success"}` OR `{kind: "otp-required"}`.
   */
  login: (args: UseAuthLoginArgs) => Promise<LoginResult>;
  /** Sign in with a Google ID token — same result union as `login`. */
  googleLogin: (args: UseAuthGoogleLoginArgs) => Promise<LoginResult>;
  /** Sign in with a Microsoft ID token (MSAL) — same result union as `login`. */
  microsoftLogin: (args: UseAuthMicrosoftLoginArgs) => Promise<LoginResult>;
  /** OTP completion — the access token is minted HERE, not in `login`. D-11. */
  completeOtp: (args: UseAuthCompleteOtpArgs) => Promise<{ snapshot: AuthSnapshot }>;
  /** TOTP challenge completion — the access token is minted HERE, not in `login`. */
  completeTotp: (args: UseAuthCompleteTotpArgs) => Promise<{ snapshot: AuthSnapshot }>;
  /** The workspace picked after a `workspace-required` login — same result union as `login`. */
  selectWorkspace: (args: UseAuthSelectWorkspaceArgs) => Promise<LoginResult>;
  /** Platform staff login — no slug, no OTP. AUTH-03. */
  platformLogin: (args: PlatformLoginArgs) => Promise<{ snapshot: AuthSnapshot }>;
  /** D-07 sequence — never returns a snapshot; hard-nav to loginPath. */
  logout: (args: PerformLogoutArgs) => Promise<void>;
  /** AUTH-09 — always resolves. UI swaps to a "check your email" state locally. */
  requestReset: (args: RequestResetArgs) => Promise<void>;
  /** Throws RESET_TOKEN_* on error; D-10 mandates no auto-login on success. */
  completeReset: (args: CompleteResetArgs) => Promise<void>;
  /**
   * Invite acceptance (EMP-05 / CC-52 + admin/account-owner invites). Two-step:
   *   1) POST /auth/invite/accept (X-Tenant-Slug header) — discards admin tokens
   *   2) POST /auth/login (audience injected from createAppRoot({ aud }))
   * Returns the same `LoginResult` discriminated union as `login`. The
   * audience is sourced from app context so HR Admin invitees end up with
   * aud=admin tokens and Employee Web invitees end up with aud=mobile tokens.
   */
  acceptInvite: (args: UseAuthInviteAcceptArgs) => Promise<LoginResult>;
}

export function useAuth(): UseAuthApi {
  const store = useAuthStore();
  const audience = useAuthAudience();
  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    // Server snapshot (SSR / test-env fallback) — always unauth to match a
    // fresh client boot pre-bootstrap.
    () => UNAUTH_SNAPSHOT,
  );

  const login = useCallback(
    async (args: UseAuthLoginArgs): Promise<LoginResult> => {
      const result = await performLogin({ ...args, audience: args.audience ?? audience });
      if (result.kind === "success") {
        store.set(result.snapshot);
      }
      return result;
    },
    [store, audience],
  );

  const googleLogin = useCallback(
    async (args: UseAuthGoogleLoginArgs): Promise<LoginResult> => {
      const result = await performGoogleLogin({ ...args, audience: args.audience ?? audience });
      if (result.kind === "success") {
        store.set(result.snapshot);
      }
      return result;
    },
    [store, audience],
  );

  const microsoftLogin = useCallback(
    async (args: UseAuthMicrosoftLoginArgs): Promise<LoginResult> => {
      const result = await performMicrosoftLogin({ ...args, audience: args.audience ?? audience });
      if (result.kind === "success") {
        store.set(result.snapshot);
      }
      return result;
    },
    [store, audience],
  );

  const completeOtp = useCallback(
    async (args: UseAuthCompleteOtpArgs): Promise<{ snapshot: AuthSnapshot }> => {
      const result = await completeOtpAction({ ...args, audience: args.audience ?? audience });
      store.set(result.snapshot);
      return result;
    },
    [store, audience],
  );

  const completeTotp = useCallback(
    async (args: UseAuthCompleteTotpArgs): Promise<{ snapshot: AuthSnapshot }> => {
      const result = await verifyTotpAction({ ...args, audience: args.audience ?? audience });
      store.set(result.snapshot);
      return result;
    },
    [store, audience],
  );

  const selectWorkspace = useCallback(
    async (args: UseAuthSelectWorkspaceArgs): Promise<LoginResult> => {
      const result = await performWorkspaceSelect({ ...args, audience });
      if (result.kind === "success") {
        store.set(result.snapshot);
      }
      return result;
    },
    [store, audience],
  );

  const platformLogin = useCallback(
    async (args: PlatformLoginArgs): Promise<{ snapshot: AuthSnapshot }> => {
      const result = await performPlatformLogin(args);
      store.set(result.snapshot);
      return result;
    },
    [store],
  );

  const logout = useCallback(async (args: PerformLogoutArgs): Promise<void> => {
    // No snapshot commit — performLogout ends with window.location.assign.
    await performLogoutAction(args);
  }, []);

  const acceptInvite = useCallback(
    async (args: UseAuthInviteAcceptArgs): Promise<LoginResult> => {
      const result = await performInviteAcceptAction({ ...args, audience });
      // Mirror `login`: commit the snapshot when the chained login returns
      // a success so the next render is already authenticated and the
      // protected routes render without flashing the unauth fallback.
      if (result.kind === "success") {
        store.set(result.snapshot);
      }
      return result;
    },
    [store, audience],
  );

  return {
    ...snapshot,
    login,
    googleLogin,
    microsoftLogin,
    completeOtp,
    completeTotp,
    selectWorkspace,
    platformLogin,
    logout,
    requestReset: requestResetAction,
    completeReset: completeResetAction,
    acceptInvite,
  };
}

// Re-export for callers that need just the type (e.g. tests, adapters).
export type { QueryClient };
